import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { gsap } from 'gsap';
import { 
  Download, 
  Calendar, 
  Users, 
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  BarChart3,
  Trash2,
  Shield
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LineChart from '../components/ui/LineChart';
import CustomDateRangeModal from '../components/modals/CustomDateRangeModal';
import DeleteGameConfirmationModal from '../components/modals/DeleteGameConfirmationModal';
import PermissionGuard from '../components/ui/PermissionGuard';
import { apiService, Registration, DeleteGameRequest } from '../services/api';
import { downloadBlob, generateFilename } from '../utils/downloadHelper';
import { usePermissions } from '../contexts/PermissionContext';

const GameDetailsPage: React.FC = () => {
  const { company, game, gameType } = useParams<{ company: string; game: string; gameType: string }>();
  const { isLoading: authLoading, isAuthenticated } = useAuth0();
  const { adminRoles, isLoading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingDate, setDownloadingDate] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingCustom, setDownloadingCustom] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingGame, setDeletingGame] = useState(false);
  const [gameDetails, setGameDetails] = useState<{originalGame: string, originalGameType: string} | null>(null);
  const [apiReady, setApiReady] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  const rowsRef = useRef<HTMLTableRowElement[]>([]);

  // Decode URL parameters first
  const decodedGame = game ? decodeURIComponent(game) : '';
  const decodedGameType = gameType ? decodeURIComponent(gameType) : '';
  const currentCompany = (company || 'unknown').toLowerCase();

  // Find the original case game name by fetching games list and matching lowercase
  const findOriginalGameCase = useCallback(async () => {
    try {
      const games = await apiService.getGames(currentCompany);
      const matchingGame = games.find(g => 
        g.game.toLowerCase() === decodedGame.toLowerCase() && 
        g.game_type.toLowerCase() === decodedGameType.toLowerCase()
      );
      
      if (matchingGame) {
        setGameDetails({
          originalGame: matchingGame.game,
          originalGameType: matchingGame.game_type
        });
        return { originalGame: matchingGame.game, originalGameType: matchingGame.game_type };
      }
      
      // Fallback to decoded values if no match found
      setGameDetails({
        originalGame: decodedGame,
        originalGameType: decodedGameType
      });
      return { originalGame: decodedGame, originalGameType: decodedGameType };
    } catch (error) {
      console.error('Error finding original game case:', error);
      // Fallback to decoded values
      setGameDetails({
        originalGame: decodedGame,
        originalGameType: decodedGameType
      });
      return { originalGame: decodedGame, originalGameType: decodedGameType };
    }
  }, [currentCompany, decodedGame, decodedGameType]);

  const fetchRegistrations = useCallback(async () => {
    if (!game || !gameType) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // First find the original case for the game names
      const originalCase = await findOriginalGameCase();
      
      // Use original case for API call
      const registrationsData = await apiService.getRegistrations(
        originalCase.originalGame, 
        originalCase.originalGameType,
        currentCompany
      );
      setRegistrations(registrationsData);
    } catch (err: any) {
      console.error('Error fetching registrations:', err);
      setError(
        err.response?.status === 401 
          ? 'Unauthorized access. Please check your permissions.'
          : err.response?.status === 403
          ? 'Forbidden. You do not have permission to view registrations.'
          : err.response?.status === 404
          ? 'Game not found.'
          : 'Failed to load registrations. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [game, gameType, findOriginalGameCase, currentCompany]);

  // Monitor API service token state
  useEffect(() => {
    const checkApiReady = () => {
      const hasToken = apiService.hasAuthToken();
      setApiReady(hasToken);
      return hasToken;
    };

    // Check immediately
    checkApiReady();

    // Set up an interval to periodically check if API has token
    // This handles the race condition where AuthWrapper sets token asynchronously
    const interval = setInterval(() => {
      if (!apiReady && isAuthenticated && !authLoading) {
        checkApiReady();
      }
    }, 100); // Check every 100ms

    return () => clearInterval(interval);
  }, [isAuthenticated, authLoading, apiReady]);

  useEffect(() => {
    // Only fetch registrations when everything is ready
    if (!authLoading && !permissionsLoading && isAuthenticated && apiReady) {
      console.log('🔐 Auth ready and API has token, fetching registrations...');
      fetchRegistrations();
    } else {
      console.log('⏳ Waiting for auth to be ready...', {
        authLoading,
        permissionsLoading,
        isAuthenticated,
        apiReady
      });
    }
  }, [fetchRegistrations, authLoading, permissionsLoading, isAuthenticated, apiReady]);

  useEffect(() => {
    if (registrations.length > 0 && rowsRef.current.length > 0) {
      gsap.fromTo(rowsRef.current,
        { x: -20, opacity: 0 },
        { 
          x: 0, 
          opacity: 1,
          duration: 0.4,
          stagger: 0.05,
          ease: 'power2.out'
        }
      );
    }
  }, [registrations]);

  // Check if URL needs to be redirected to lowercase (after all hooks)
  if (company && company !== company.toLowerCase()) {
    const lowercaseCompany = company.toLowerCase();
    console.log(`🔄 Redirecting from /games/${company} to /games/${lowercaseCompany}`);
    return <Navigate to={`/games/${lowercaseCompany}/${game}/${gameType}`} replace />;
  }
  
  if (game && decodedGame !== decodedGame.toLowerCase()) {
    const lowercaseGame = encodeURIComponent(decodedGame.toLowerCase());
    console.log(`🔄 Redirecting game from ${game} to ${lowercaseGame}`);
    return <Navigate to={`/games/${company}/${lowercaseGame}/${gameType}`} replace />;
  }
  
  if (gameType && decodedGameType !== decodedGameType.toLowerCase()) {
    const lowercaseGameType = encodeURIComponent(decodedGameType.toLowerCase());
    console.log(`🔄 Redirecting gameType from ${gameType} to ${lowercaseGameType}`);
    return <Navigate to={`/games/${company}/${game}/${lowercaseGameType}`} replace />;
  }

  const handleDownloadSingleDate = async (date: string) => {
    if (!game || !gameType || !gameDetails) return;
    
    try {
      setDownloadingDate(date);
      const blob = await apiService.downloadSingleDateCSV(
        gameDetails.originalGame, 
        gameDetails.originalGameType, 
        date,
        currentCompany
      );
      const filename = generateFilename(gameDetails.originalGame, gameDetails.originalGameType, date);
      downloadBlob(blob, filename);
    } catch (err: any) {
      console.error('Error downloading CSV:', err);
      alert(
        err.response?.status === 404
          ? 'No data available for this date.'
          : 'Failed to download CSV. Please try again.'
      );
    } finally {
      setDownloadingDate(null);
    }
  };

  const handleCustomDateDownload = async (startDate: string, endDate: string) => {
    if (!game || !gameType || !gameDetails) return;
    
    try {
      setDownloadingCustom(true);
      const blob = await apiService.downloadCustomRangeCSV(
        gameDetails.originalGame, 
        gameDetails.originalGameType, 
        {
          startDate,
          endDate
        },
        currentCompany
      );
      const filename = generateFilename(gameDetails.originalGame, gameDetails.originalGameType, undefined, true);
      downloadBlob(blob, filename);
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error downloading custom CSV:', err);
      alert(
        err.response?.status === 400
          ? 'Invalid date range selected.'
          : err.response?.status === 404
          ? 'No data available for the selected date range.'
          : 'Failed to download CSV. Please try again.'
      );
    } finally {
      setDownloadingCustom(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!gameDetails) return;
    
    try {
      setDeletingGame(true);
      
      const deleteGameRequest: DeleteGameRequest = {
        game: gameDetails.originalGame,
        game_type: gameDetails.originalGameType,
        company: currentCompany
      };
      
      console.log('🗑️ Deleting game:', deleteGameRequest);
      
      await apiService.deleteGame(deleteGameRequest);
      
      console.log('✅ Game deleted successfully');
      
      // Close the modal and navigate back to games list
      setShowDeleteModal(false);
      navigate(`/games/${currentCompany}`);
      
    } catch (err: any) {
      console.error('❌ Error deleting game:', err);
      // Show error message to user
      alert(
        err.message || 'Failed to delete game. Please try again.'
      );
    } finally {
      setDeletingGame(false);
    }
  };

  const totalPlayers = registrations.reduce((sum, reg) => sum + reg.players, 0);
  const hasRecords = totalPlayers > 0;
  
  // Use original case for display if available, otherwise use decoded (which should be lowercase from URL)
  const displayGame = gameDetails?.originalGame || decodedGame;
  const displayGameType = gameDetails?.originalGameType || decodedGameType;

  // Show loading while auth is not ready or while fetching data
  if (authLoading || permissionsLoading || !apiReady || (loading && isAuthenticated)) {
    return (
      <Layout title={`${displayGame} - ${displayGameType}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <LoadingSpinner size="large" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              {authLoading || permissionsLoading 
                ? 'Authenticating...' 
                : !apiReady
                ? 'Setting up API connection...'
                : 'Loading registrations...'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={`${displayGame} - ${displayGameType}`}>
        <div className="space-y-6">
          <button
            onClick={() => navigate(`/games/${currentCompany}`)}
            className="btn-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Games
          </button>
          
          <div className="flex items-center justify-center h-64">
            <div className="text-center p-8 card max-w-md">
              <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Error Loading Registrations
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </p>
              <button
                onClick={fetchRegistrations}
                className="btn-primary"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`${displayGame} - ${displayGameType}`}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/games/${currentCompany}`)}
              className="btn-secondary"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Games
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {displayGame}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 capitalize">
                {displayGameType} • Registration Data
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={fetchRegistrations}
              className="btn-secondary"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <PermissionGuard 
              permissions={['download:registrations']} 
              roles={adminRoles}
              showFallback={false}
            >
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn-primary"
              >
                <Download className="w-4 h-4" />
                Custom Range
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Days</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {registrations.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Players</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {totalPlayers.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg. per Day</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {registrations.length > 0 ? Math.round(totalPlayers / registrations.length) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Trends Chart */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Registration Trends
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Daily registration data for {displayGame}
                  </p>
                </div>
              </div>
              {registrations.length > 0 && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {totalPlayers.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total registrations
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6">
            {registrations.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    No registration data available for chart
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <LineChart
                  data={registrations.map(reg => ({
                    date: reg.date,
                    value: reg.players
                  }))}
                  title="Daily Registrations"
                  height={300}
                  className="mb-4"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {registrations.length}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Days with data
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {registrations.length > 0 ? Math.round(totalPlayers / registrations.length) : 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Avg. per day
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {registrations.length > 0 ? Math.max(...registrations.map(r => r.players)).toLocaleString() : 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Peak day
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Registrations Table */}
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Daily Registrations
            </h3>
          </div>
          
          {registrations.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No Registrations Found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                There are no registrations available for this game.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table ref={tableRef} className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    <th className="table-header">Date</th>
                    <th className="table-header">Players</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
                  {registrations.map((registration, index) => (
                    <tr
                      key={registration.date}
                      ref={el => rowsRef.current[index] = el!}
                      className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors"
                    >
                      <td className="table-cell font-medium">
                        {new Date(registration.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="table-cell">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                          {registration.players.toLocaleString()} players
                        </span>
                      </td>
                      <td className="table-cell">
                        <PermissionGuard 
                          permissions={['download:registrations']} 
                          roles={adminRoles}
                          showFallback={false}
                          fallback={
                            <span className="text-sm text-gray-400 dark:text-gray-500">
                              No permission
                            </span>
                          }
                        >
                          <button
                            onClick={() => handleDownloadSingleDate(registration.date)}
                            disabled={downloadingDate === registration.date}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-900/30 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {downloadingDate === registration.date ? (
                              <LoadingSpinner size="small" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            Download CSV
                          </button>
                        </PermissionGuard>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <PermissionGuard permissions={['delete:games']} showFallback={false}>
          <div className="bg-white dark:bg-dark-800 rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 bg-gradient-to-r from-red-50 to-red-50/70 dark:from-red-950/30 dark:to-red-950/10 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center shadow-sm">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                    Danger Zone
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-300 leading-relaxed">
                    Irreversible and destructive actions
                  </p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Delete this game
                  </h4>
                  {hasRecords ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      Cannot delete <span className="font-medium text-gray-900 dark:text-gray-100">{displayGame}</span> because it contains registration data. Games with existing records cannot be deleted to preserve data integrity.
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      Permanently delete <span className="font-medium text-gray-900 dark:text-gray-100">{displayGame}</span> and all associated data.
                    </p>
                  )}
                  
                  {/* Warning/Info Box */}
                  <div className={hasRecords 
                    ? "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4" 
                    : "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4"
                  }>
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 mt-0.5 flex-shrink-0 ${hasRecords 
                        ? "text-blue-600 dark:text-blue-400" 
                        : "text-amber-600 dark:text-amber-400"
                      }`}>
                        {hasRecords ? (
                          <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.19-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium mb-2 ${hasRecords 
                          ? "text-blue-800 dark:text-blue-200" 
                          : "text-amber-800 dark:text-amber-200"
                        }`}>
                          {hasRecords ? "This game currently contains:" : "This will permanently delete:"}
                        </p>
                        <ul className={`text-sm space-y-1 ${hasRecords 
                          ? "text-blue-700 dark:text-blue-300" 
                          : "text-amber-700 dark:text-amber-300"
                        }`}>
                          <li className="flex items-center gap-2">
                            <span className={`w-1 h-1 rounded-full flex-shrink-0 ${hasRecords 
                              ? "bg-blue-600 dark:bg-blue-400" 
                              : "bg-amber-600 dark:bg-amber-400"
                            }`}></span>
                            All registration data ({totalPlayers.toLocaleString()} total registrations)
                          </li>
                          <li className="flex items-center gap-2">
                            <span className={`w-1 h-1 rounded-full flex-shrink-0 ${hasRecords 
                              ? "bg-blue-600 dark:bg-blue-400" 
                              : "bg-amber-600 dark:bg-amber-400"
                            }`}></span>
                            {registrations.length} days of historical data
                          </li>
                          <li className="flex items-center gap-2">
                            <span className={`w-1 h-1 rounded-full flex-shrink-0 ${hasRecords 
                              ? "bg-blue-600 dark:bg-blue-400" 
                              : "bg-amber-600 dark:bg-amber-400"
                            }`}></span>
                            Download history and analytics
                          </li>
                          <li className="flex items-center gap-2">
                            <span className={`w-1 h-1 rounded-full flex-shrink-0 ${hasRecords 
                              ? "bg-blue-600 dark:bg-blue-400" 
                              : "bg-amber-600 dark:bg-amber-400"
                            }`}></span>
                            Game configuration and settings
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Delete Button */}
                <div className="flex-shrink-0">
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    disabled={deletingGame || hasRecords}
                    className={`px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-dark-800 ${
                      hasRecords 
                        ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                        : "bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-red-400 disabled:cursor-not-allowed text-white hover:shadow-md focus:ring-red-500 disabled:opacity-50"
                    }`}
                    title={hasRecords ? "Cannot delete game with existing registration data" : "Delete this game permanently"}
                  >
                    <Trash2 className="w-4 h-4" />
                    {hasRecords ? "Cannot Delete" : "Delete Game"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>

        {/* Custom Date Range Modal */}
        <CustomDateRangeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onDownload={handleCustomDateDownload}
          loading={downloadingCustom}
        />

        {/* Delete Game Confirmation Modal */}
        <DeleteGameConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteGame}
          loading={deletingGame}
          gameName={displayGame}
          gameType={displayGameType}
          companyName={currentCompany}
        />
      </div>
    </Layout>
  );
};

export default GameDetailsPage;
